import type { TTemplateVariant } from '@/types/templates'
import {
  repositionAndRescaleImageElement,
  repositionAndRescaleElement,
  adjustH1FontSize,
} from '@/helpers/coverVariantResize'
import { cssSelectors, setTitleOnPreview } from '@/helpers/templatePreviewHelperForCovers'
import type { TAmazonElement, TAmazonElementText } from '@/types/amazon-cover'
import type { TProject } from '@/types/projects'

const pageVariantTextContent: {
  title?: string
  subtitle?: string
  author?: string
  summary?: string
} = {
  title: '',
  subtitle: '',
  author: '',
  summary: '',
}

function createStyleElementForAdjustingPages(doc: Document, variantClasses: string[]): HTMLStyleElement {
  const style = doc.createElement('style')
  style.textContent = `
    html body div.cover-page-container,
    html body div.back-page-container {
      margin: 0 !important;
      margin-top: 0px !important;
    }

    html body {
      margin: 0 !important;
      margin-top: 0px !important;
    }
  `

  variantClasses.forEach((variantClass) => {
    style.textContent += `
      html body div.variant.${variantClass},
      html body div.variant.${variantClass}.cover-page-container,
      html body div.variant.${variantClass}.back-page-container {
        margin: 0 !important;
        margin-top: 0px !important;
      }
    `
  })

  return style
}

export function findPageVariantAndAdjustH1FontSize(
  iframe: HTMLIFrameElement,
  project: TProject,
  type: 'cover' | 'back',
  newCoverTitle: string,
): void {
  const iframeWindow = iframe?.contentDocument?.defaultView
  if (!iframeWindow) {
    return
  }

  setTitleOnPreview(iframe, newCoverTitle)
  const pageVariants = project?.sortedVariants?.div?.[`${type}-page-container`]

  if (pageVariants?.length && newCoverTitle?.length) {
    const firstPage = iframe?.contentDocument?.querySelector(`.${type}-page-container`)
    const isHtmlElement = firstPage instanceof iframeWindow.HTMLElement
    const pageVariantClass = firstPage && isHtmlElement ? getVariantClassFromPage(firstPage) : ''

    if (isHtmlElement && pageVariantClass?.length) {
      const variantToUse = pageVariants.find((variant) => variant.element_selector.includes(pageVariantClass))
      if (variantToUse) {
        adjustH1FontSize(firstPage, variantToUse, getFirstPageSizePixels(iframe), newCoverTitle)
      }
    }
  }
}

export function getVariantClassFromPage(page: HTMLElement): string | null {
  return [...page.classList].find((c) => c.startsWith('_div_')) || null
}

export function prepareProjectHtmlForThumbnailGeneration(previewIframe?: HTMLIFrameElement): string {
  const previewHtml = previewIframe?.contentDocument?.querySelector('html')?.outerHTML
  const htmlStart = '<!DOCTYPE html>'
  if (!previewHtml) {
    return htmlStart + '<html><head></head><body></body></html>'
  }

  const parser = new DOMParser()
  const docSend = parser.parseFromString(previewHtml ?? '', 'text/html')

  //remove regular page containers
  docSend.querySelectorAll('div.page-container').forEach((el) => el.remove())

  //remove margins of body
  if (docSend.body instanceof HTMLElement) {
    docSend.body.style.setProperty('margin', '0', 'important')
  }

  const coverAndBackPages = docSend.querySelectorAll('.cover-page-container, .back-page-container')
  const variantClasses: string[] = []

  //make text containers transparent for image generation and remove margin top
  coverAndBackPages?.forEach((p) => {
    makeTextContainersTransparent(p as HTMLElement, p.classList.contains('cover-page-container') ? 'cover' : 'back')

    if (p instanceof HTMLElement) {
      //ensure pages have 0 margin to generate correct images
      p.style.setProperty('margin-top', '0', 'important')
      p.style.setProperty('margin-left', '0', 'important')

      const variantClass: string | null = getVariantClassFromPage(p as HTMLElement)
      if (variantClass) {
        variantClasses.push(variantClass)
      }
    }

    //remove placeholder image elements before image generation DES-4122
    const imagePlaceholders: NodeListOf<Element> = p.querySelectorAll('.absolute-image-placeholder')
    imagePlaceholders.forEach((img: Element): void => {
      if (img instanceof HTMLElement) {
        img.remove()
      }
    })
  })

  //set styles in head to ensure no margins for pages in multiple cases
  docSend.head.appendChild(createStyleElementForAdjustingPages(docSend, variantClasses))
  //remove unecessary stylesheets that could break image thumbnail
  docSend.head.querySelectorAll('link[href*="nice-preview.css"]').forEach((l) => l.remove())

  return htmlStart + docSend.documentElement.outerHTML
}

function findAmazonElementById(content: TAmazonElement[], id: string): TAmazonElementText | undefined {
  return content.find((obj) => obj.id === id) as TAmazonElementText
}

function actualizeTextContent(amazonElement: TAmazonElementText, selector: string, page: HTMLElement): void {
  if (amazonElement?.textContent?.length) {
    const domElement = page.querySelector(selector)
    if (domElement) {
      domElement.textContent = amazonElement.textContent
    }
  }
}

function fillPreviewFrameWithTextFromStore(coverPage: HTMLElement, backPage: HTMLElement, content: TAmazonElement[]) {
  const frontTitle: TAmazonElement = findAmazonElementById(content, 'front-title') as TAmazonElementText
  actualizeTextContent(frontTitle, cssSelectors.title, coverPage)

  const author: TAmazonElement = findAmazonElementById(content, 'front-author') as TAmazonElementText
  actualizeTextContent(author, cssSelectors.author, coverPage)

  const subtitle: TAmazonElement = findAmazonElementById(content, 'front-subtitle') as TAmazonElementText
  actualizeTextContent(subtitle, cssSelectors.subtitle, coverPage)

  const summary: TAmazonElement = findAmazonElementById(content, 'back-summary') as TAmazonElementText
  actualizeTextContent(summary, cssSelectors.summary, backPage)
}

export async function setPreviewIframeWithCurrentText(
  iframe: HTMLIFrameElement,
  content: TAmazonElement[],
): Promise<boolean> {
  return new Promise((resolve) => {
    whenIframeReady(iframe, async () => {
      const doc = iframe.contentDocument
      if (doc) {
        const coverPage = doc.querySelector(`.cover-page-container`) as HTMLElement
        const backPage = doc.querySelector(`.back-page-container`) as HTMLElement
        if (!coverPage || !backPage) {
          resolve(false)
        }
        fillPreviewFrameWithTextFromStore(coverPage, backPage, content)
        resolve(true)
      } else {
        resolve(false)
      }
    })
  })
}

export async function changePageVariant(
  iframe: HTMLIFrameElement,
  newVariant: TTemplateVariant,
  type: 'cover' | 'back',
): Promise<boolean> {
  return new Promise((resolve) => {
    whenIframeReady(iframe, async () => {
      const doc = iframe.contentDocument
      if (doc) {
        const page = doc.querySelector(`.${type}-page-container`) as HTMLElement
        if (!page) {
          resolve(false)
        }
        await switchPageVariant(page, newVariant, type)
        resolve(true)
      } else {
        resolve(false)
      }
    })
  })
}

export function clearVariantStylesFromHiddenIframe(iframe: HTMLIFrameElement) {
  const page = iframe.contentDocument?.querySelector('.cover-page-container, .back-page-container') as HTMLElement
  if (!page) {
    return
  }

  const doc = page.ownerDocument
  const styleId = 'variant-styles'
  const style = doc.getElementById(styleId)

  if (style) {
    style.remove()
  }
}

function appendVariantCSSToHiddenIframe(page: HTMLElement, newVariant: TTemplateVariant) {
  const doc = page.ownerDocument
  const styleId = 'variant-styles'

  const style =
    (doc.getElementById(styleId) as HTMLStyleElement | null) ??
    (() => {
      const s = doc.createElement('style')
      s.id = styleId
      doc.head.appendChild(s)
      return s
    })()

  const newCssParts = [
    newVariant.css,
    ...(newVariant.inner_variants?.map((iv) => iv.inner_variant?.css).filter(Boolean) || []),
  ]

  style.textContent += '\n\n' + newCssParts.join('\n\n')
}

export function replaceVariantClass(page: HTMLElement, newVariantSelector: string) {
  const classList = Array.from(page.classList)
  const variantClass = classList.find((cls) => cls.startsWith('_div_'))
  if (variantClass) {
    page.classList.remove(variantClass)
  }
  page.classList.add(newVariantSelector.replace('.variant.', ''))
}

function clearPageInlineStyles(page: HTMLElement) {
  page.style.backgroundColor = ''
  page.style.backgroundImage = ''
  page.style.backgroundSize = ''
  page.style.backgroundPosition = ''
  page.style.backgroundRepeat = ''
  page.style.background = ''
}

async function switchPageVariant(
  page: HTMLElement,
  newVariant: TTemplateVariant,
  type: 'cover' | 'back',
): Promise<void> {
  return new Promise((resolve) => {
    appendVariantCSSToHiddenIframe(page, newVariant)
    clearPageInlineStyles(page)
    replaceVariantClass(page, newVariant.element_selector)
    // wait for the browser to apply the new styles before manipulating DOM
    requestAnimationFrame(() => {
      //clear text content state first
      setPageVariantTextContent('', '', '', '')
      //remember current values
      rememberCurrentPageVariantTextContent(page, type)
      removeAbsoluteElements(page)
      appendAbsoluteElementsFromNewVariant(page, newVariant, type)
      applyPageVariantTextContent(page, type)
      resizeAndRepositionAbsoluteElements(page, newVariant.entire_page_height, newVariant.entire_page_width)
      adjustH1FontSize(page, newVariant, getActualPageSize(page))
      makeTextContainersTransparent(page, type)
      resolve()
    })
  })
}

export function makeTextContainersTransparent(page: HTMLElement, type: 'cover' | 'back') {
  const elements: HTMLElement[] = []

  const titleElement = page.querySelector(cssSelectors.title)
  if (titleElement) {
    elements.push(titleElement as HTMLElement)
  }

  const authorElement = page.querySelector(cssSelectors.author)
  if (authorElement) {
    elements.push(authorElement as HTMLElement)
  }

  if (type === 'cover') {
    const subtitleElements = page.querySelectorAll(cssSelectors.subtitle)
    if (subtitleElements?.[0]) elements.push(subtitleElements[0] as HTMLElement)
    if (subtitleElements?.[1]) elements.push(subtitleElements[1] as HTMLElement)
  }

  if (type === 'back') {
    const summaryElement = page.querySelector(cssSelectors.summary)
    if (summaryElement) elements.push(summaryElement as HTMLElement)
  }

  elements.forEach((el) => {
    let parent = el.parentElement
    el.style.background = 'transparent'
    el.style.backgroundImage = 'none'

    while (
      parent &&
      !parent.classList.contains('cover-page-container') &&
      !parent.classList.contains('back-page-container')
    ) {
      parent.style.background = 'transparent'
      parent.style.backgroundImage = 'none'
      parent = parent.parentElement
    }
  })
}

export function getFirstPageSizePixels(iframe: HTMLIFrameElement | null): { height: number; width: number } {
  const toReturn = {
    height: 1123,
    width: 794,
  }

  if (!iframe || !iframe.contentDocument) {
    return toReturn
  }

  const firstPage = iframe.contentDocument.querySelector('.cover-page-container, .back-page-container')
  if (!firstPage) {
    return toReturn
  }

  const actualPageSize = getActualPageSize(firstPage as HTMLElement)
  return actualPageSize ?? toReturn
}

export function getActualPageSize(page: HTMLElement): { width: number; height: number } | null {
  const pageBody = page.closest('body')
  if (!pageBody) {
    return null
  }

  const pageSize = pageBody.getAttribute('data-page-size')

  if (!pageSize) {
    return null
  }

  const [actualPageWidth = 0, actualPageHeight = 0] = pageSize.split('x').map((value) => Math.round(parseFloat(value)))
  return { width: actualPageWidth, height: actualPageHeight }
}

export function resizeAndRepositionAbsoluteElements(page: HTMLElement, originalHeight: number, originalWidth: number) {
  const absoluteElements = Array.from(page.children) as HTMLElement[]
  const actualPageSize = getActualPageSize(page)
  const actualPageHeight = actualPageSize?.height ?? 0
  const actualPageWidth = actualPageSize?.width ?? 0

  absoluteElements.forEach((element: HTMLElement) => {
    if (element.tagName === 'IMG') {
      repositionAndRescaleImageElement(page, element, originalHeight, originalWidth, actualPageHeight, actualPageWidth)
    } else {
      repositionAndRescaleElement(page, element, originalHeight, originalWidth, actualPageHeight, actualPageWidth)
    }
  })
}

function removeAbsoluteElements(page: HTMLElement) {
  //all elements in cover and back page are direct and absolute
  const children = Array.from(page.children)
  children.forEach((child) => child.remove())
}

function appendAbsoluteElementsFromNewVariant(page: HTMLElement, newVariant: TTemplateVariant, type: 'cover' | 'back') {
  const parsedNewVariantHtml = new DOMParser().parseFromString(newVariant.html, 'text/html')
  const parseError = parsedNewVariantHtml.querySelector('parsererror')

  if (parseError) {
    console.error('Error parsing HTML', parseError.textContent)
    return
  } else if (!parsedNewVariantHtml.body || !parsedNewVariantHtml.body.children.length) {
    console.error('No body and children in new variant HTML')
    return
  }

  const newVariantPage = parsedNewVariantHtml.querySelector(`.${type}-page-container`) as HTMLElement
  const newPageChildren = Array.from(newVariantPage.children)
  newPageChildren.forEach((newPageChild) => page.appendChild(newPageChild))
}

function whenIframeReady(iframe: HTMLIFrameElement, callback: () => void) {
  function check() {
    const doc = iframe.contentDocument
    if (doc && doc.readyState === 'complete' && doc.querySelector('.cover-page-container')) {
      callback()
    } else {
      setTimeout(check, 50)
    }
  }
  check()
}

function applyPageVariantTextContent(page: HTMLElement, type: 'cover' | 'back') {
  const titleElement = page.querySelector(cssSelectors.title)
  if (titleElement && pageVariantTextContent.title) {
    titleElement.textContent = pageVariantTextContent.title
  }

  const authorElement = page.querySelector(cssSelectors.author)
  if (authorElement && pageVariantTextContent.author) {
    authorElement.textContent = pageVariantTextContent.author
  }

  if (type === 'cover') {
    const subtitleElement = page.querySelector(cssSelectors.subtitle)
    if (subtitleElement && pageVariantTextContent.subtitle) {
      subtitleElement.textContent = pageVariantTextContent.subtitle
    }
  }

  if (type === 'back') {
    const summaryElement = page.querySelector(cssSelectors.summary)
    if (summaryElement && pageVariantTextContent.summary) {
      summaryElement.textContent = pageVariantTextContent.summary
    }
  }
}

function rememberCurrentPageVariantTextContent(page: HTMLElement, type: 'cover' | 'back') {
  const title = page.querySelector(cssSelectors.title)?.textContent ?? ''
  const subtitleFromPage = page.querySelector(cssSelectors.subtitle)?.textContent
  const subtitle = type === 'cover' && subtitleFromPage ? subtitleFromPage : ''
  const author = page.querySelector(cssSelectors.author)?.textContent ?? ''
  const summaryFromPage = page.querySelector(cssSelectors.summary)?.textContent
  const summary = type === 'back' && summaryFromPage ? summaryFromPage : ''

  setPageVariantTextContent(title, subtitle, author, summary)
}

function setPageVariantTextContent(title: string, subtitle: string, author: string, summary: string) {
  pageVariantTextContent.title = title
  pageVariantTextContent.subtitle = subtitle
  pageVariantTextContent.author = author
  pageVariantTextContent.summary = summary
}

export function generateGoogleFontsLinkAllWeights(fonts: string[]): string {
  const baseUrl = 'https://fonts.googleapis.com/css2?'
  const weights =
    'ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900'

  const families = fonts.map((font) => `family=${encodeURIComponent(font)}:${weights}`).join('&')

  return `${baseUrl}${families}&display=swap`
}
